import React, { createContext, useContext, useReducer } from 'react';

// 🔧 輔助函數：生成文件 ID
const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// 初始狀態 - 純檔案陣列（移除業務屬性）
const initialFileState = [
  // 檔案結構（純檔案資訊，不含業務邏輯）：
  // {
  //   // === 基本檔案屬性 ===
  //   id: 'file_1234', // 檔案唯一標識
  //   name: 'audio.mp3',
  //   url: 'blob://local-url' | 'https://server.com/blob/id',
  //   size: 1024,
  //   content_type: 'audio/mp3', // MIME 類型
  //   fileSource: 'server' | 'local' | 'library', // 檔案來源
  //   status: 'uploading' | 'processing' | 'done' | 'error', // 檔案狀態
  //   uploadProgress: 0, // 上傳進度 (0-100)
  //   processingStatus: '', // 處理狀態描述
  //   errorMessage: '', // 錯誤訊息
  //   retryCount: 0, // 重試次數
  //   createdAt: '2024-01-01T10:00:00Z',
  //
  //   // === 條件屬性（根據檔案類型） ===
  //   duration: '2:30', // 僅音訊檔案
  //   preview: 'blob://preview-url', // 僅圖片/影片
  //   originalWidth: 1920, // 僅圖片
  //   originalHeight: 1080, // 僅圖片
  //   
  //   // === 上傳相關 ===
  //   originFileObj: File, // 本地檔案對象（僅本地檔案）
  //   serverBlobId: 'real-blob-id-from-server', // 僅伺服器檔案
  //   response: {}, // 上傳回應
  //
  //   // === 🆕 檔案位置追蹤（用於備用匹配）===
  //   formField: ['ceremony', 'public', 'files', 'my', 'audio'], // 表單欄位位置
  //   uploadContext: {
  //     section: 'public', // 業務區段
  //     category: 'my', // 檔案分類
  //     fileType: 'audio', // 檔案類型
  //     order: 0 // 在該位置的順序（用於多檔案時的匹配）
  //   }
  // }
];

// 🎯 檔案管理 Action Types
const FileActionTypes = {
  ADD_FILE: 'ADD_FILE',
  REMOVE_FILE: 'REMOVE_FILE',
  UPDATE_FILE: 'UPDATE_FILE',
  CLEAR_ALL_FILES: 'CLEAR_ALL_FILES',
  RESET_FILES: 'RESET_FILES',
  UPDATE_FILE_STATUS: 'UPDATE_FILE_STATUS',
  RETRY_FILE: 'RETRY_FILE'
};

// 🎯 檔案管理 Reducer（純檔案邏輯）
const fileReducer = (state, action) => {
  switch (action.type) {
    case FileActionTypes.ADD_FILE: {
      const newFile = {
        ...action.payload,
        id: action.payload.id || generateFileId(),
        fileSource: action.payload.fileSource || 'local',
        status: action.payload.status || 'uploading',
        uploadProgress: 0,
        processingStatus: '',
        errorMessage: '',
        retryCount: 0,
        createdAt: new Date().toISOString(),
        formField: action.payload.formField || [],
        uploadContext: action.payload.uploadContext || {}
      };
      return [...state, newFile];
    }
    case FileActionTypes.REMOVE_FILE:
      return state.filter(file => file.id !== action.payload);
    case FileActionTypes.UPDATE_FILE:
      return state.map(file => 
        file.id === action.payload.id
          ? { ...file, ...action.payload.updates }
          : file
      );
    case FileActionTypes.UPDATE_FILE_STATUS:
      return state.map(file => 
        file.id === action.payload.id
          ? {
              ...file,
              status: action.payload.status,
              uploadProgress: action.payload.uploadProgress || file.uploadProgress,
              processingStatus: action.payload.processingStatus || file.processingStatus,
              errorMessage: action.payload.errorMessage || file.errorMessage
            }
          : file
      );
    case FileActionTypes.RETRY_FILE:
      return state.map(file => 
        file.id === action.payload.id
          ? {
              ...file,
              status: 'uploading',
              uploadProgress: 0,
              errorMessage: '',
              retryCount: (file.retryCount || 0) + 1
            }
          : file
      );
    case FileActionTypes.CLEAR_ALL_FILES: {
      return [];
    }
    case FileActionTypes.RESET_FILES:
      return initialFileState;
    default:
      return state;
  }
};

// Context
const FileManagementContext = createContext();

// Provider Component
export const FileManagementProvider = ({ children }) => {
  const [state, dispatch] = useReducer(fileReducer, []);

  const actions = {
    addFile: (file) => {
      dispatch({ type: FileActionTypes.ADD_FILE, payload: file });
    },
    removeFile: (fileId) => {
      dispatch({ type: FileActionTypes.REMOVE_FILE, payload: fileId });
    },
    updateFile: (id, updates) => {
      dispatch({ type: FileActionTypes.UPDATE_FILE, payload: { id, updates } });
    },
    getFile: (fileId) => {
      return state.find(file => file.id === fileId);
    },
    addServerFile: (file) => {
      dispatch({
        type: FileActionTypes.ADD_FILE,
        payload: {
          ...file,
          fileSource: 'server',
          status: 'done'
        }
      });
    },
    addLibraryFile: (file) => {
      dispatch({
        type: FileActionTypes.ADD_FILE,
        payload: {
          ...file,
          fileSource: 'library',
          status: 'done'
        }
      });
    },
    clearAllFiles: () => dispatch({
      type: FileActionTypes.CLEAR_ALL_FILES
    }),
    resetFiles: () => dispatch({ type: FileActionTypes.RESET_FILES }),
    updateFileStatus: (id, statusInfo) => {
      dispatch({
        type: FileActionTypes.UPDATE_FILE_STATUS,
        payload: { id, ...statusInfo }
      });
    },
    retryFile: (id) => {
      dispatch({
        type: FileActionTypes.RETRY_FILE,
        payload: { id }
      });
    }
  };

  return (
    <FileManagementContext.Provider value={{ state, actions }}>
      {children}
    </FileManagementContext.Provider>
  );
};

// 基礎 Hook
export const useFileManagementContext = () => {
  const context = useContext(FileManagementContext);
  if (!context) {
    throw new Error('useFileManagementContext must be used within a FileManagementProvider');
  }
  return context;
};

// 🎯 檔案 Selector Hooks（簡化版，移除業務邏輯）

/**
 * 獲取所有檔案的陣列
 */
export const useFiles = () => {
  const { state } = useFileManagementContext();
  return state;
};

/**
 * 根據 ID 獲取特定檔案 (核心查詢方法)
 */
export const useFileById = (fileId) => {
  const { state } = useFileManagementContext();
  return state.find(file => file.id === fileId) || null;
};

/**
 * 根據多個 ID 獲取檔案列表
 */
export const useFilesByIds = (fileIds = []) => {
  const { state } = useFileManagementContext();
  return fileIds
    .map(fileId => state.find(file => file.id === fileId))
    .filter(Boolean);
};

/**
 * 獲取需要上傳的檔案 (local 和 library)
 */
export const useFilesToUpload = () => {
  const { state } = useFileManagementContext();
  return state.filter(file => {
    switch (file.fileSource) {
      case 'local':
      case 'library':
        return true;
      case 'server':
      default:
        return false;
    }
  });
};

/**
 * 獲取伺服器檔案
 */
export const useServerFiles = () => {
  const { state } = useFileManagementContext();
  return state.filter(file => file.fileSource === 'server');
};

/**
 * 根據檔案類型獲取檔案
 */
export const useFilesByType = (contentType) => {
  const { state } = useFileManagementContext();
  return state.filter(file => file.content_type?.startsWith(contentType));
};

/**
 * 獲取檔案統計信息
 */
export const useFileStats = () => {
  const { state } = useFileManagementContext();

  return {
    total: state.length,
    byType: state.reduce((acc, file) => {
      const type = file.content_type?.split('/')[0] || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {}),
    bySource: state.reduce((acc, file) => {
      acc[file.fileSource] = (acc[file.fileSource] || 0) + 1;
      return acc;
    }, {}),
    localCount: state.filter(f => f.fileSource === 'local').length,
    libraryCount: state.filter(f => f.fileSource === 'library').length,
    serverCount: state.filter(f => f.fileSource === 'server').length,
    uploadNeededCount: state.filter(f => f.fileSource === 'local' || f.fileSource === 'library').length
  };
};

/**
 * 根據 ID 查找檔案（向後兼容，原本的 useFileByUid）
 */
export const useFileByUid = (id) => {
  const { state } = useFileManagementContext();
  return state.find(file => file.id === id) || null;
};

/**
 * 獲取特定狀態的檔案
 */
export const useFilesByStatus = (status) => {
  const { state } = useFileManagementContext();
  return state.filter(file => file.status === status);
};

/**
 * 獲取所有檔案 actions (方便組件使用)
 */
export const useFileManagementActions = () => {
  const { actions } = useFileManagementContext();
  return actions;
};

/**
 * 組合 Hook：同時獲取狀態和操作
 */
export const useFileManagementFull = () => {
  const { state, actions } = useFileManagementContext();
  return { state, actions };
};

// 📝 設計說明：
// FileManagementContext 現在專注於純檔案管理，不包含業務邏輯
// 業務屬性（section/category/type）將由 DemandContext 管理
// 這實現了更好的關注點分離和單一職責原則 